import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Names, Values } from '@mode-switch/model';
import { EncField, EncFieldGroupType, Encoding, MarkType, VariableType } from '@mode-vegas/vegas';
import { EncodingHelpers, FieldFormatHelpers } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { Subscription } from 'rxjs';
import { FormatListItem, makeFormatListItemFromField, makeFormatListItemFromGroup } from './format-list-item';

const emptyFieldList: FormatListItem[] = [];

/*
 * TODO: Move this into lib chart helper or state somewhere
 */
export function buildFieldList(
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  chartType: ChartTypes.ChartType,
  fieldFormats: ChartTypes.FieldFormatMap
): FormatListItem[] {
  if (!(view && encoding)) {
    return emptyFieldList;
  }

  const legendFieldKeys = new Set();

  let fields = [
    ...encoding.column,
    ...encoding.x,
    ...encoding.x2,
    ...encoding.row,
    ...encoding.y,
    ...encoding.y2,
    ...encoding.marks.properties.color.values,
    ...encoding.marks.properties.size.values,
    ...encoding.marks.properties.text.values,
    ...encoding.marks.properties.detail.values,
    ...encoding.marks.properties.tooltip.values,
    ...encoding.marks.properties.angle.values,
    ...encoding.marks.properties.baseline.values,
  ];

  const hasMeasureValues = EncodingHelpers.isInEncoding(encoding, Values.Key);
  if (hasMeasureValues) {
    fields.push(...encoding.value);
  }

  [...encoding.marks.properties.color.values, ...encoding.marks.properties.size.values]
    .map((f) => FieldFormatHelpers.getFormatMapKeyByField(f))
    .forEach((k) => legendFieldKeys.add(k));

  for (const series of encoding.marks.series) {
    fields.push(
      ...series.properties.color.values,
      ...series.properties.size.values,
      ...series.properties.text.values,
      ...series.properties.detail.values,
      ...series.properties.tooltip.values,
      ...series.properties.angle.values,
      ...series.properties.baseline.values
    );

    [...series.properties.color.values, ...series.properties.size.values]
      .map((f) => FieldFormatHelpers.getFormatMapKeyByField(f))
      .forEach((k) => legendFieldKeys.add(k));
  }

  const filterFields = (fieldsToFilter: string[]) => {
    const filterOutSystemFields = (field: EncField) => {
      if (fieldsToFilter.indexOf(field.formula) === -1) {
        return field;
      }
    };
    return fields.filter(filterOutSystemFields);
  };

  switch (chartType) {
    case 'vegasPivotTable':
      {
        const excludeFields = [Names.Key, Values.Key];
        fields = filterFields(excludeFields);
      }
      break;
    default:
      break;
  }

  const uniqueMappedFields = new Map<string, FormatListItem>();
  for (const field of fields) {
    const key = FieldFormatHelpers.getFormatMapKeyByField(field);
    if (!uniqueMappedFields.has(key)) {
      const showLegend = legendFieldKeys.has(key);
      uniqueMappedFields.set(key, makeFormatListItemFromField(view, field, encoding, showLegend, fieldFormats));
    }
  }

  // Add the groups
  encoding.groups?.forEach((group) => {
    const groupFormatInfo = makeFormatListItemFromGroup(view, group, encoding, fieldFormats);
    uniqueMappedFields.set(groupFormatInfo.formatKey, groupFormatInfo);
  });

  return Array.from(uniqueMappedFields.values());
}

enum PanelStateEnum {
  Open,
  Closed,
}

type PanelType = 'Axis' | 'Header' | 'Pane' | 'Legend' | 'DisplayName';
@Component({
  selector: 'mode-format-builder',
  templateUrl: './format-builder.component.html',
  styleUrls: ['./format-builder.component.scss'],
})
export class FormatBuilderComponent implements OnChanges, OnDestroy {
  @Input() chartService: any;
  @Input() observables: any;
  @Input() encoding!: Encoding;
  @Input() view!: FlamingoTypes.ModeView;
  @Input() format!: ChartTypes.ChartFormat;
  @Input() fieldFormats!: ChartTypes.FieldFormatMap;
  @Input() chartType!: ChartTypes.ChartType;
  @Input() selectedFieldToFormat: string | null = null;

  PanelState = PanelStateEnum;

  fieldList: FormatListItem[] = [];
  selectedField: FormatListItem | undefined;

  selectedFieldSuggestedBinInfo?: BinSizeTypes.BinSizeMetaData;
  binSizeToFieldMapSubscription?: Subscription;

  axisFormat: ChartTypes.AxisFieldFormat | undefined;
  headerFormat: ChartTypes.HeaderFieldFormat | undefined;
  legendFormat: ChartTypes.LegendFieldFormat | undefined;
  paneFormat: ChartTypes.PaneFieldFormat | undefined;
  baseFormat: ChartTypes.BaseFieldFormat | undefined = undefined;

  isSelectedFieldOnDiscreteAxis = false;
  isSelectedFieldBaselineOnly = false;

  panelStates = {
    Axis: this.PanelState.Open,
    Header: this.PanelState.Open,
    Pane: this.PanelState.Open,
    Legend: this.PanelState.Open,
    DisplayName: this.PanelState.Open,
  };

  constructor() {}

  ngOnDestroy(): void {
    this.binSizeToFieldMapSubscription?.unsubscribe();
  }

  updateDisplayName(displayName: ChartTypes.BaseFieldFormat) {
    if (this.selectedField) {
      this.chartService.updateBaseFormatDisplayName(
        this.selectedField.fieldFormula,
        this.selectedField.variableType,
        displayName
      );
    }
  }

  updateAxis(axis: ChartTypes.AxisFieldFormat) {
    if (this.selectedField) {
      this.chartService.updateAxisFormat(this.selectedField.fieldFormula, VariableType.CONTINUOUS, axis);
    }
  }

  updateHeader(header: ChartTypes.HeaderFieldFormat) {
    if (this.selectedField) {
      this.chartService.updateHeaderFormat(this.selectedField.fieldFormula, VariableType.DISCRETE, header);
    }
  }

  updateLegend(legend: ChartTypes.ChartLegend) {
    if (this.selectedField) {
      this.chartService.updateLegendFormat(this.selectedField.fieldFormula, this.selectedField.variableType, legend);
    }
  }

  updatePane(pane: ChartTypes.PaneFieldFormat) {
    if (this.selectedField) {
      this.chartService.updatePaneFormat(this.selectedField.fieldFormula, this.selectedField.variableType, pane);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.fieldList = buildFieldList(this.view, this.encoding, this.chartType, this.fieldFormats);
    if (changes.selectedFieldToFormat && this.selectedFieldToFormat !== null) {
      const formatListItem = this.fieldList.find((f) => f.formatKey === this.selectedFieldToFormat);
      this.onFieldSelected(formatListItem);
    } else if (this.selectedField) {
      // Since the field list was rebuilt, the selected field is stale
      this.selectedField = this.fieldList.find((f) => f.formatKey === this.selectedField?.formatKey);
    }
    this.isSelectedFieldOnDiscreteAxis = this.isDiscreteAxis();
    this.isSelectedFieldBaselineOnly = this.isBaselineOnly();
    this.updateFormatFields();
  }

  onFieldSelected(field?: FormatListItem) {
    this.selectedField = field;
    this.isSelectedFieldOnDiscreteAxis = this.isDiscreteAxis();
    this.isSelectedFieldBaselineOnly = this.isBaselineOnly();
    this.updateFormatFields();
    this.updatePanelStates(field);
  }

  updatePanelStates(selectedField?: FormatListItem) {
    if (!selectedField) {
      return;
    }
    if (selectedField.type === EncFieldGroupType.SHARED_AXIS) {
      this.panelStates.Axis = this.PanelState.Open;
      this.panelStates.Pane = this.PanelState.Closed;
    } else if (selectedField.isContinuous) {
      const axisRelatedFields = [...this.encoding.x, ...this.encoding.y, ...this.encoding.y2, ...this.encoding.x2];
      const inAxis = axisRelatedFields.some((field) => selectedField.fieldFormula === field.formula);
      const markFields =
        this.encoding.marks.channel === '*'
          ? [
              ...this.encoding.marks.properties.color.values,
              ...this.encoding.marks.properties.detail.values,
              ...this.encoding.marks.properties.size.values,
              ...this.encoding.marks.properties.text.values,
              ...this.encoding.marks.properties.angle.values,
              ...this.encoding.marks.properties.baseline.values,
            ]
          : this.encoding.marks.series.reduce((out, series) => {
              out.push(
                ...series.properties.color.values,
                ...series.properties.detail.values,
                ...series.properties.size.values,
                ...series.properties.text.values,
                ...series.properties.angle.values,
                ...series.properties.baseline.values
              );
              return out;
            }, [] as Readonly<EncField>[]);
      const inMarks = markFields.some((field) => selectedField.fieldFormula === field.formula);
      this.panelStates.Axis = inAxis ? this.PanelState.Open : this.PanelState.Closed;
      this.panelStates.Pane = inAxis && !inMarks ? this.PanelState.Closed : this.PanelState.Open;
    } else {
      this.panelStates.Axis = this.PanelState.Open;
      this.panelStates.Pane = this.PanelState.Open;
    }
  }

  updateFormatFields() {
    if (this.fieldFormats && this.selectedField) {
      const fieldFormatKey = this.selectedField.formatKey;
      const formats = this.fieldFormats[fieldFormatKey] ?? ({} as ChartTypes.FieldFormat);
      this.axisFormat = formats.axisFormat ?? {};
      this.headerFormat = formats.headerFormat ?? {};
      this.legendFormat = formats.legendFormat ?? {};
      this.paneFormat = formats.paneFormat ?? {};
      this.baseFormat = formats.baseFormat ?? {};
    } else {
      this.axisFormat = undefined;
      this.headerFormat = undefined;
      this.legendFormat = undefined;
      this.paneFormat = undefined;
    }
  }

  panelStateChange(panel: PanelType, state: PanelStateEnum) {
    this.panelStates[panel] = state;
  }

  isDiscreteAxis() {
    // Current field being formatted must be discrete
    if (!this.selectedField?.isDiscrete) {
      return false;
    }
    // For pivot tables, it is never treated as an axis
    if (this.encoding.marks.channel === '*' && this.encoding.marks.properties.type.value === MarkType.Text) {
      return false;
    }
    // Get the axis fields to check. We only want discrete fields
    // so no need to check x2/y2.
    const axisFields = (() => {
      if (this.encoding.marks.channel === '*') {
        return [...this.encoding.x, ...this.encoding.y];
      }
      return this.encoding.marks.channel === 'x' ? [...this.encoding.y] : [...this.encoding.x];
    })();
    // Find a field that matches the current selected fields formula.
    const matchedField = axisFields.find((f) => f.formula === this.selectedField?.fieldFormula);
    // If found, ensure that the EncField is also discrete
    return matchedField?.variable.type === VariableType.DISCRETE;
  }

  isBaselineOnly() {
    if (!this.selectedField) {
      return false;
    }
    // Gather all of the baseline fields in the encoding
    const baselineFields =
      this.encoding.marks.channel === '*'
        ? this.encoding.marks.properties.baseline.values
        : this.encoding.marks.series.flatMap((series) => series.properties.baseline.values);

    // Check if the selected field formula is on any baseline channel
    if (baselineFields.find((field) => field.formula === this.selectedField?.fieldFormula) == null) {
      // Not a baseline field
      return false;
    }
    // If our field is on baseline, check if it is anywhere else in the encoding. To do that,
    // remove the baseline fields and search for the formula.
    const modifiedEncoding = EncodingHelpers.removeFieldsFromEncoding(
      this.encoding,
      baselineFields.map((field) => field.id ?? '')
    ).encoding;
    // If the formula is still in the encoding, then return false.
    return EncodingHelpers.findInEncoding(modifiedEncoding, this.selectedField.fieldFormula) == null;
  }
}
